import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import { Plus, Trash, ClipboardText, Lightning, Users, Note, FileXls } from '@phosphor-icons/react';

const COLS = [
  { key: 'consumer_id', label: 'Consumer ID', width: 'w-36', placeholder: 'LPG cons #' },
  { key: 'mobile', label: 'Mobile', width: 'w-32', placeholder: '98xxxxxxxx' },
  { key: 'customer_name', label: 'Customer Name', width: 'w-44', placeholder: 'Optional' },
  { key: 'amount', label: 'Amount', width: 'w-24', type: 'number', placeholder: '0' },
  { key: 'notes', label: 'Notepad / Notes', width: 'w-72', placeholder: 'Free notes — paste/type anything' },
];

const blankRow = () => ({ consumer_id: '', mobile: '', customer_name: '', amount: '', notes: '', sub_staff_id: '' });

export default function DistributeDialog({ open, onOpenChange, file, onDone }) {
  const [subs, setSubs] = useState([]);
  const [selectedSubIds, setSelectedSubIds] = useState(() => new Set());  // Item #13 (Feb 2026): tick-based eligibility
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  // Smart Card Row Split (Feb 2026): chunk_size > 0 enables split mode.
  // Default = agency_rate × 5 = one group worth (5 bookings).
  const [chunkSize, setChunkSize] = useState(0);
  const [chunkEnabled, setChunkEnabled] = useState(false);
  const tableRef = useRef(null);

  // Initial load
  const [agencyRate, setAgencyRate] = useState(0);

  useEffect(() => {
    if (!open) return;
    api.get('/staff/my-substaff').then(r => {
      const list = r.data || [];
      setSubs(list);
      // Default: all sub-staff ticked. User can untick to exclude.
      setSelectedSubIds(new Set(list.map(s => s.id)));
    });
    // Per-user-request (Feb 2026): pre-populate dialog with agency-provided
    // file data (LPG ID + Mobile + Name) so staff doesn't have to paste it.
    // Also pre-fills amount from agency booking_rate.
    if (file?.id) {
      api.get(`/booking-execution/${file.id}/preview-source`)
        .then(res => {
          const sourceRows = res.data?.rows || [];
          const rate = Number(res.data?.booking_rate) || Number(file?.booking_rate) || 0;
          setAgencyRate(rate);
          // Auto-default chunk_size = rate × 5 (one booking group)
          if (rate > 0) setChunkSize(prev => prev || rate * 5);
          if (sourceRows.length > 0) {
            setRows(sourceRows.map(r => ({
              consumer_id: r.consumer_id || '',
              customer_name: r.customer_name || '',
              mobile: r.mobile || '',
              amount: Number(r.amount) > 0 ? Number(r.amount) : rate,
              sub_staff_id: '',
            })));
          } else {
            // No data in file — start with 8 blank rows pre-filled with rate.
            setRows(Array.from({ length: 8 }, () => ({ ...blankRow(), amount: rate })));
          }
        })
        .catch(() => {
          const rate = Number(file?.booking_rate) || 0;
          setAgencyRate(rate);
          setRows(Array.from({ length: 8 }, () => ({ ...blankRow(), amount: rate })));
        });
    } else {
      setRows(Array.from({ length: 8 }, blankRow));
    }
  }, [open, file?.id, file?.booking_rate]);

  // Helpers for tick selection
  const toggleSubSelection = (id) => {
    setSelectedSubIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllSubs = () => setSelectedSubIds(new Set(subs.map(s => s.id)));
  const selectOnlySubs = (ids) => setSelectedSubIds(new Set(ids));
  const isSelected = (id) => selectedSubIds.has(id);

  const totalRows = rows.length;
  const filledRows = rows.filter(r => (r.consumer_id || '').trim() || (r.mobile || '').trim()).length;
  const assignedRows = rows.filter(r => r.sub_staff_id && ((r.consumer_id || '').trim() || (r.mobile || '').trim())).length;

  const updateCell = (idx, key, val) => {
    setRows(prev => prev.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  };

  const addRow = () => setRows(prev => [...prev, { ...blankRow(), amount: agencyRate }]);
  const addManyRows = (n) => setRows(prev => [...prev, ...Array.from({ length: n }, () => ({ ...blankRow(), amount: agencyRate }))]);
  const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));
  const clearAll = () => setRows(Array.from({ length: 8 }, blankRow));

  // Auto-equal split — assigns each row to a sub-staff round-robin.
  // Item #13 (Feb 2026): respects tick-selection pool. Unticked sub-staff
  // are NEVER assigned. Online/offline gating removed per earlier user spec.
  const autoEqualSplit = () => {
    const pool = subs.filter(s => selectedSubIds.has(s.id));
    if (pool.length === 0) { toast.error('Pehle kam se kam 1 sub-staff tick karo'); return; }
    const filled = rows.map((r, i) => ({ r, i })).filter(({ r }) => (r.consumer_id || '').trim() || (r.mobile || '').trim());
    if (filled.length === 0) { toast.error('Pehle data paste/type karo'); return; }

    setRows(prev => prev.map((r, i) => {
      const orderIdx = filled.findIndex(f => f.i === i);
      if (orderIdx === -1) return r;
      return { ...r, sub_staff_id: pool[orderIdx % pool.length].id };
    }));
    const onlineCnt = pool.filter(s => s.is_online).length;
    const offCnt = pool.length - onlineCnt;
    toast.success(`${filled.length} rows ${pool.length} selected sub-staff me split (${onlineCnt} online · ${offCnt} offline)`);
  };

  // Bulk assign all filled rows to one sub-staff
  const assignAllTo = (subId) => {
    setRows(prev => prev.map(r => {
      if (!(r.consumer_id || '').trim() && !(r.mobile || '').trim()) return r;
      return { ...r, sub_staff_id: subId };
    }));
  };

  // Smart paste: if user pastes multi-line/multi-column data into ANY cell,
  // intelligently distribute across columns and rows
  const handlePaste = useCallback((e, rowIdx, colKey) => {
    const text = e.clipboardData?.getData('text/plain') || '';
    if (!text) return;
    // Detect if this is a structured paste (multiple lines OR tabs)
    const lines = text.split(/\r?\n/).filter(l => l !== '' || true).slice(0, 500);
    if (lines.length <= 1 && !text.includes('\t')) {
      // Single value — let default handle
      return;
    }
    e.preventDefault();
    const colKeys = COLS.map(c => c.key);
    const startCol = colKeys.indexOf(colKey);
    const cleanLines = lines.filter(l => l.trim());

    setRows(prev => {
      const next = [...prev];
      // Ensure capacity
      while (next.length < rowIdx + cleanLines.length) next.push(blankRow());
      cleanLines.forEach((line, li) => {
        const cells = line.split('\t');
        const target = next[rowIdx + li] || blankRow();
        cells.forEach((val, ci) => {
          const cKey = colKeys[startCol + ci];
          if (cKey) target[cKey] = val.trim();
        });
        next[rowIdx + li] = target;
      });
      return next;
    });
    toast.success(`${cleanLines.length} rows pasted from spreadsheet`);
  }, []);

  const submit = async () => {
    const valid = rows.filter(r => (r.consumer_id || '').trim() || (r.mobile || '').trim());
    if (valid.length === 0) { toast.error('Koi row nahi bhari'); return; }
    const unassigned = valid.filter(r => !r.sub_staff_id);
    if (unassigned.length > 0) {
      toast.error(`${unassigned.length} rows me sub-staff assign nahi hai. "Auto-Equal Split" use karo ya manually choose karo.`);
      return;
    }
    // Item #13: Block submit if any row is assigned to a sub-staff NOT in the
    // ticked eligibility pool. Forces user to either tick that sub-staff or
    // reassign the row.
    const offPool = valid.filter(r => !selectedSubIds.has(r.sub_staff_id));
    if (offPool.length > 0) {
      const offNames = [...new Set(offPool.map(r => subs.find(s => s.id === r.sub_staff_id)?.name || 'Unknown'))];
      toast.error(`${offPool.length} rows ${offNames.join(', ')} ko assign hai jo selected pool me nahi. Tick karo ya unassign karo.`);
      return;
    }
    setBusy(true);
    try {
      const body = {
        allocations: [],  // empty — backend will derive from per-row sub_staff_id
        rows: valid.map(r => ({
          consumer_id: (r.consumer_id || '').trim(),
          mobile: (r.mobile || '').trim(),
          customer_name: (r.customer_name || '').trim(),
          amount: Number(r.amount || 0),
          notes: (r.notes || '').trim(),
          sub_staff_id: r.sub_staff_id,
        })),
        chunk_size: chunkEnabled ? Number(chunkSize || 0) : 0,
      };
      const { data } = await api.post(`/booking-files/${file.id}/distribute`, body);
      let msg = `Distributed: ${data.created} rows across ${data.allocations} sub-staff`;
      if (data.chunk_summary?.chunks_created > 0) {
        const cs = data.chunk_summary;
        const breakdown = Object.entries(cs.chunks_assigned || {}).map(([sid, n]) => {
          const sub = subs.find(s => s.id === sid);
          return `${sub?.name || sid.slice(0, 6)}: ${n}`;
        }).join(' · ');
        msg += `\n📦 ${cs.chunks_created} card chunks: ${breakdown}`;
      }
      toast.success(msg);
      onOpenChange(false);
      onDone && onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Distribute failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardText size={22} weight="duotone" className="text-violet-600" />
            Distribute to Team — Spreadsheet Mode
          </DialogTitle>
        </DialogHeader>

        <div className="bg-violet-50 border border-violet-200 rounded p-2 text-[11px]">
          📋 <b>File:</b> {file?.original_filename} · <b>Total expected:</b> {file?.total_bookings_count || 0}
          <span className="ml-2 text-violet-700">
            · Tip: Excel/Google Sheets se data copy karke kisi bhi cell pe paste karo — sab columns auto-fill ho jayenge.
          </span>
        </div>

        {subs.length === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded p-4 text-sm text-amber-800">
            Aapke under koi sub-staff nahi hai. Admin se add karwao.
          </div>
        ) : (
          <>
            {/* Item #13 (Feb 2026): Eligibility tick-list — only ticked sub-staff
                will receive rows via Auto-Equal Split or per-row select. */}
            <div className="bg-blue-50 border-2 border-blue-300 rounded p-2 mb-2">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-xs font-bold text-blue-900">
                  ✅ Eligible Sub-Staff Pool ({selectedSubIds.size}/{subs.length} selected)
                </span>
                <span className="text-[10px] text-gray-600">— sirf ticked sub-staff ko distribute hoga</span>
                <div className="ml-auto flex gap-1">
                  <button type="button" onClick={selectAllSubs} className="text-[10px] px-2 py-0.5 rounded border bg-white hover:bg-blue-100 border-blue-300" data-testid="select-all-subs">
                    Select All
                  </button>
                  <button type="button" onClick={() => selectOnlySubs(subs.filter(s => s.is_online).map(s => s.id))} className="text-[10px] px-2 py-0.5 rounded border bg-white hover:bg-blue-100 border-blue-300" data-testid="select-online-only">
                    Online Only
                  </button>
                  <button type="button" onClick={() => setSelectedSubIds(new Set())} className="text-[10px] px-2 py-0.5 rounded border bg-white hover:bg-rose-100 border-rose-300 text-rose-700" data-testid="select-none-subs">
                    Clear
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {subs.map(s => (
                  <label
                    key={s.id}
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border cursor-pointer transition-colors ${isSelected(s.id) ? (s.is_online ? 'bg-emerald-100 border-emerald-400 text-emerald-900' : 'bg-gray-200 border-gray-400 text-gray-800') : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}
                    data-testid={`sub-pool-${s.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected(s.id)}
                      onChange={() => toggleSubSelection(s.id)}
                      className="w-3 h-3 accent-emerald-600"
                      data-testid={`sub-pool-check-${s.id}`}
                    />
                    <span className="text-[11px] font-bold">
                      {s.is_online ? '🟢' : '⚫'} {s.name}
                      {!s.is_online && <span className="ml-1 text-[9px] font-normal italic">(offline)</span>}
                    </span>
                  </label>
                ))}
              </div>
              {selectedSubIds.size === 0 && (
                <div className="mt-1.5 text-[10px] text-rose-700 font-bold">
                  ⚠️ Kam se kam 1 sub-staff tick karo — bina selection ke distribute nahi hoga
                </div>
              )}
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-2 flex-wrap py-2 sticky top-0 bg-white z-10 border-b">
              <Button size="sm" variant="outline" onClick={() => addRow()} className="h-7 text-xs gap-1" data-testid="add-row-btn">
                <Plus size={12} /> Row
              </Button>
              <Button size="sm" variant="outline" onClick={() => addManyRows(20)} className="h-7 text-xs" data-testid="add-20-btn">
                + 20 rows
              </Button>
              <Button size="sm" variant="outline" onClick={autoEqualSplit} className="h-7 text-xs gap-1 text-amber-700 border-amber-300" data-testid="auto-split-btn">
                <Lightning size={12} weight="fill" /> Auto-Equal Split
              </Button>
              <select
                onChange={e => { if (e.target.value) { assignAllTo(e.target.value); e.target.value = ''; } }}
                className="text-xs border rounded h-7 px-2"
                defaultValue=""
                data-testid="assign-all-select"
              >
                <option value="">Assign all filled rows to…</option>
                {subs.filter(s => isSelected(s.id)).map(s => <option key={s.id} value={s.id}>{s.is_online ? '🟢 ' : '⚫ '}{s.name}{!s.is_online ? ' (offline)' : ''}</option>)}
              </select>
              <span className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded border bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="online-count-pill" title="Sub-staff online in last 5 min">
                🟢 {subs.filter(s => s.is_online).length} online
                {subs.filter(s => !s.is_online).length > 0 && (
                  <span className="text-gray-500 ml-1">· ⚫ {subs.filter(s => !s.is_online).length} offline</span>
                )}
              </span>
              <div className="ml-auto text-[11px] text-gray-600">
                <Users size={12} className="inline mr-0.5" /> {filledRows} filled · {assignedRows} assigned · {totalRows} rows
              </div>
              <Button
                size="sm"
                onClick={submit}
                disabled={busy || filledRows === 0 || subs.length === 0 || assignedRows < filledRows}
                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white gap-1 font-bold"
                data-testid="top-submit-distribute-btn"
                title={assignedRows < filledRows ? `${filledRows - assignedRows} rows still unassigned` : 'Distribute now'}
              >
                {busy ? 'Distributing…' : `✓ Submit ${assignedRows}`}
              </Button>
              <Button size="sm" variant="ghost" onClick={clearAll} className="h-7 text-xs text-red-600">Clear</Button>
            </div>

            {/* Spreadsheet Grid */}
            <div className="border rounded overflow-x-auto" ref={tableRef}>
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-1 py-1.5 text-center w-8 text-[10px] font-bold text-gray-500">#</th>
                    {COLS.map(c => (
                      <th key={c.key} className={`px-2 py-1.5 text-left text-[10px] uppercase font-bold text-gray-600 ${c.width}`}>
                        {c.key === 'notes' && <Note size={10} className="inline mr-1" />}
                        {c.label}
                      </th>
                    ))}
                    <th className="px-2 py-1.5 text-left text-[10px] uppercase font-bold text-gray-600 w-44">Sub-Staff</th>
                    <th className="px-1 py-1.5 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => {
                    const isFilled = (r.consumer_id || '').trim() || (r.mobile || '').trim();
                    const needsAssignment = isFilled && !r.sub_staff_id;
                    return (
                      <tr key={idx} className={`border-t ${needsAssignment ? 'bg-amber-50/40' : ''}`}>
                        <td className="px-1 py-0.5 text-center text-[10px] text-gray-400 font-mono">{idx + 1}</td>
                        {COLS.map(c => (
                          <td key={c.key} className="px-0.5 py-0.5">
                            {c.key === 'notes' ? (
                              <textarea
                                rows={1}
                                value={r[c.key] || ''}
                                onChange={(e) => updateCell(idx, c.key, e.target.value)}
                                onPaste={(e) => handlePaste(e, idx, c.key)}
                                placeholder={c.placeholder}
                                className="w-full h-7 px-1.5 py-1 text-xs border-0 focus:ring-1 focus:ring-violet-400 rounded resize-y min-h-[28px] max-h-32 bg-yellow-50/30"
                                data-testid={`cell-${idx}-${c.key}`}
                              />
                            ) : c.key === 'amount' ? (
                              <input
                                type="number"
                                value={r.amount || ''}
                                readOnly
                                title="Agency rate — admin sets, staff cannot edit"
                                className="w-full h-7 px-1.5 text-xs border-0 rounded bg-gray-100 text-gray-700 font-bold text-right cursor-not-allowed"
                                data-testid={`cell-${idx}-${c.key}`}
                              />
                            ) : (
                              <input
                                type={c.type || 'text'}
                                value={r[c.key] || ''}
                                onChange={(e) => updateCell(idx, c.key, e.target.value)}
                                onPaste={(e) => handlePaste(e, idx, c.key)}
                                placeholder={c.placeholder}
                                className="w-full h-7 px-1.5 text-xs border-0 focus:ring-1 focus:ring-violet-400 rounded"
                                data-testid={`cell-${idx}-${c.key}`}
                              />
                            )}
                          </td>
                        ))}
                        <td className="px-0.5 py-0.5">
                          <select
                            value={r.sub_staff_id || ''}
                            onChange={(e) => updateCell(idx, 'sub_staff_id', e.target.value)}
                            className={`w-full h-7 px-1 text-xs border-0 rounded ${r.sub_staff_id ? 'text-violet-700 font-medium' : 'text-gray-400'}`}
                            data-testid={`row-sub-${idx}`}
                          >
                            <option value="">— select —</option>
                            {/* Item #13: Only show ticked sub-staff in dropdown */}
                            {subs.filter(s => isSelected(s.id)).map(s => <option key={s.id} value={s.id}>{s.is_online ? '🟢 ' : '⚫ '}{s.name}{!s.is_online ? ' (offline)' : ''}</option>)}
                          </select>
                        </td>
                        <td className="px-1 py-0.5 text-center">
                          {rows.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeRow(idx)}
                              className="text-red-500 hover:text-red-700 p-0.5"
                              tabIndex={-1}
                              aria-label="Remove row"
                            >
                              <Trash size={11} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Per-staff summary */}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {subs.map(s => {
                const cnt = rows.filter(r => r.sub_staff_id === s.id && ((r.consumer_id || '').trim() || (r.mobile || '').trim())).length;
                if (cnt === 0) return null;
                return (
                  <span key={s.id} className={`text-[10px] px-2 py-0.5 rounded font-medium ${s.is_online ? 'bg-violet-100 text-violet-800' : 'bg-gray-200 text-gray-700'}`}>
                    {s.is_online ? '🟢 ' : '⚫ '}{s.name}: {cnt}{!s.is_online ? ' (offline)' : ''}
                  </span>
                );
              })}
              {assignedRows === 0 && (
                <span className="text-[10px] text-gray-400 italic">Koi assignment nahi — Auto-Equal Split try karo</span>
              )}
            </div>

            <div className="text-[10px] text-gray-500 italic mt-1">
              <FileXls size={11} className="inline mr-1" />
              Excel/Google Sheets se columns ke order me copy karo: Consumer ID · Mobile · Name · Amount · Notes — paste anywhere, baaki auto-fill ho jayega.
            </div>
          </>
        )}

        {/* Smart Card Row Split (Feb 2026) — toggle + chunk_size input */}
        <div className="bg-violet-50 border-2 border-violet-300 rounded p-3 space-y-2" data-testid="chunk-split-panel">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={chunkEnabled}
                  onChange={(e) => setChunkEnabled(e.target.checked)}
                  className="w-4 h-4"
                  data-testid="chunk-enable-toggle"
                />
                <span className="font-bold text-violet-900 text-sm">📦 Smart Card Row Split</span>
                <span className="text-[10px] text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded">RECOMMENDED</span>
              </label>
              <div className="text-[11px] text-violet-700 ml-6 mt-0.5">
                Cards ko chunks me split karo (har chunk = ek booking unit). Round-robin sub_staff me assign honge — koi short nahi padega.
              </div>
            </div>
          </div>
          {chunkEnabled && (
            <div className="flex items-center gap-2 ml-6 flex-wrap" data-testid="chunk-config">
              <label className="text-xs font-semibold text-gray-700">Chunk size ₹:</label>
              <input
                type="number"
                value={chunkSize}
                min="0"
                step="100"
                onChange={(e) => setChunkSize(Number(e.target.value || 0))}
                className="w-28 px-2 py-1 border rounded text-sm"
                data-testid="chunk-size-input"
              />
              <span className="text-[10px] text-gray-500">
                {agencyRate > 0 && (
                  <>= {Math.round((chunkSize || 0) / agencyRate)} bookings per chunk (rate ₹{agencyRate})</>
                )}
              </span>
              {agencyRate > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 text-[10px]"
                  onClick={() => setChunkSize(agencyRate * 5)}
                  data-testid="chunk-preset-5"
                >5/group (₹{agencyRate * 5})</Button>
              )}
              {agencyRate > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 text-[10px]"
                  onClick={() => setChunkSize(agencyRate)}
                  data-testid="chunk-preset-1"
                >1/booking (₹{agencyRate})</Button>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || filledRows === 0 || subs.length === 0 || assignedRows < filledRows}
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="confirm-distribute-btn"
          >
            {busy ? 'Distributing…' : `Distribute ${assignedRows} rows`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
